import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import { 
  HiRocketLaunch,
  HiCodeBracket,
  HiCube,
  HiEye,
  HiCloud,
  HiShieldCheck,
  HiCog,
  HiWrenchScrewdriver,
  HiCheckCircle,
  HiServer
} from 'react-icons/hi2';

const heroImage = 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?auto=format&fit=crop&w=1600&q=80';

const heroStats = [
  { label: 'Deployment Time', value: '-80%' },
  { label: 'System Reliability', value: '+95%' },
  { label: 'Developer Productivity', value: '+150%' }
];

const capabilities = [
              {
                title: 'CI/CD Pipeline Setup',
                description: 'Build robust continuous integration and deployment pipelines for faster, reliable releases.',
                icon: HiRocketLaunch
              },
              {
                title: 'Infrastructure as Code',
                description: 'Manage infrastructure using code with Terraform, Ansible, and other IaC tools.',
                icon: HiCodeBracket
              },
              {
                title: 'Container Orchestration',
                description: 'Deploy and manage containerized applications with Kubernetes and Docker.',
                icon: HiCube
              },
              {
                title: 'Monitoring & Logging',
                description: 'Implement comprehensive monitoring, logging, and alerting solutions.',
                icon: HiEye
              },
              {
                title: 'Cloud Platform Management',
                description: 'Optimize cloud resources and implement best practices across AWS, Azure, and GCP.',
                icon: HiCloud
              },
              {
                title: 'Security Integration',
                description: 'Integrate security practices into your DevOps workflows and infrastructure.',
                icon: HiShieldCheck
              }
];

const processSteps = [
                {
                  title: 'Assessment & Planning',
                  description: 'Evaluate current processes and create a comprehensive DevOps strategy.',
                  icon: HiEye
                },
                {
                  title: 'Tool Selection',
                  description: 'Choose and configure the right tools for your specific needs.',
                  icon: HiCog
                },
                {
                  title: 'Implementation',
                  description: 'Implement CI/CD pipelines and infrastructure automation.',
                  icon: HiCodeBracket
                },
                {
                  title: 'Optimization',
                  description: 'Continuously optimize processes and monitor performance.',
                  icon: HiWrenchScrewdriver
                }
];

const technologies = [
  'Kubernetes', 'Docker', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'Terraform',
  'Ansible', 'Helm', 'Prometheus', 'Grafana', 'ELK Stack', 'AWS', 'Azure',
  'Google Cloud', 'Istio', 'ArgoCD', 'Flux'
];

const benefits = [
  'Faster Deployments',
  'Improved Reliability',
  'Automated Workflows',
  'Infrastructure as Code',
  'Continuous Monitoring',
  'Cost Optimization'
];

const DevOpsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="DevOps & Platform Engineering"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
                <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
                >
              <div className="inline-flex items-center px-4 py-2 rounded-full border border-white/40 text-sm font-semibold text-white mb-4">
                <IconWrapper icon={HiServer} className="w-5 h-5 mr-2" />
                DevOps & Platform Engineering
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Streamline Development and Operations</h1>
              <p className="text-xl text-white/85 mb-4">Streamline your development and operations with automated workflows, infrastructure as code, and comprehensive monitoring solutions.</p>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Accelerate your software delivery with our comprehensive DevOps and platform engineering services that enable faster, more reliable deployments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
                  Get Started
                </button>
                <button className="px-8 py-4 border border-white/70 rounded-full font-semibold text-white hover:bg-white/10 transition-colors">
                  Talk to Expert
                </button>
                    </div>
                  </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-3xl border border-slate-200/80 dark:border-white/30 bg-white/90 dark:bg-slate-950/90 text-slate-900 dark:text-white shadow-xl backdrop-blur-sm p-6 space-y-5"
            >
              <p className="text-sm uppercase tracking-widest text-blue-600 dark:text-blue-300">
                What we deliver
              </p>
              <ul className="space-y-3">
                {benefits.slice(0, 4).map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700 dark:text-slate-200 text-sm">
                    <IconWrapper icon={HiCheckCircle} className="w-5 h-5 text-blue-600 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-2 gap-4 pt-5 border-t border-slate-200 dark:border-slate-800 text-sm">
                {heroStats.map((stat) => (
                  <div key={stat.label}>
                    <p className="text-slate-500 dark:text-slate-400">{stat.label}</p>
                    <p className="text-base font-semibold text-slate-900 dark:text-white">{stat.value}</p>
                  </div>
              ))}
            </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Modern DevOps Solutions</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Accelerate your software delivery with our comprehensive DevOps and platform engineering services that enable faster, more reliable deployments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 flex gap-3 shadow-sm"
              >
                <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                  <IconWrapper icon={HiCheckCircle} className="w-5 h-5" />
                </span>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                  {benefit}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our DevOps Capabilities</h2>
          </motion.div>
              
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability) => (
                  <motion.div
                key={capability.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm"
                  >
                <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 mb-4">
                  <IconWrapper icon={capability.icon} className="w-6 h-6" />
                    </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  {capability.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {capability.description}
                </p>
                  </motion.div>
                ))}
              </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our DevOps Process</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm"
              >
                <div className="text-sm font-semibold text-blue-600 dark:text-blue-300 mb-3">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 mb-4">
                  <IconWrapper icon={step.icon} className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
            className="text-center mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">DevOps Technologies</h2>
          </motion.div>
          <div className="flex flex-wrap gap-3">
            {technologies.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10">
            <div className="space-y-4">
              <p className="text-sm uppercase tracking-widest text-blue-600 dark:text-blue-300">
                Featured success
              </p>
              <h2 className="text-3xl md:text-4xl font-bold">DevOps Transformation</h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                Transformed a traditional development workflow into a modern DevOps pipeline, reducing deployment time by 80% and improving system reliability by 95%.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">-80%</p>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">
                    Deployment Time
                  </p>
                </div>
                <div className="text-center border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">+95%</p>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">
                    System Reliability
                  </p>
                </div>
                <div className="text-center border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">+150%</p>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">
                    Developer Productivity
                  </p>
                </div>
                <div className="text-center border border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">-70%</p>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">
                    Incident Response
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-slate-200 dark:border-slate-800 rounded-3xl p-10 text-center bg-white dark:bg-slate-950 shadow-sm">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your DevOps?</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
              Let's discuss how we can accelerate your software delivery and improve reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
                  Start Your Project
              </button>
              <button className="px-8 py-4 border border-slate-300 dark:border-slate-700 rounded-full font-semibold text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors">
                  Schedule Consultation
              </button>
            </div>
            </div>
        </div>
      </section>
    </div>
  );
};

export default DevOpsPage;
